/**
 * ProviderPayoutAccountPage — Datos de cobro del proveedor.
 *
 * El proveedor declara a QUÉ cuenta/wallet querer recibir el dinero del CEO.
 * El CEO ve esta info al lado del nombre del proveedor cuando va a saldar deuda.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wallet, Banknote, CreditCard, Save, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHODS = [
  { key: 'usdt_trc20', label: 'USDT (TRC20 — Tron)' },
  { key: 'usdt_bep20', label: 'USDT (BEP20 — BSC)' },
  { key: 'uyu',        label: 'Transferencia UYU (Uruguay)' },
  { key: 'usd',        label: 'Transferencia USD' },
  { key: 'cu_card',    label: 'Tarjeta cubana' },
];

const ProviderPayoutAccountPage = () => {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/providers/me/payout-account`, { headers: getAuthHeaders() });
        setForm(r.data || {});
      } catch (e) {
        toast.error(extractErrorMessage(e, 'No se pudo cargar'));
      } finally {
        setLoading(false);
      }
    })();
  }, [getAuthHeaders]);

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== '' && v !== null && v !== undefined),
      );
      await axios.put(`${API}/providers/me/payout-account`, payload, { headers: getAuthHeaders() });
      toast.success('Datos de cobro guardados ✓');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo guardar'));
    } finally {
      setSaving(false);
    }
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (loading) return <p className="text-center text-slate-500 py-12">Cargando…</p>;

  const renderField = (k, label, placeholder = '', type = 'text') => (
    <div key={k}>
      <Label htmlFor={k} className="text-xs text-slate-600">{label}</Label>
      <Input
        id={k}
        type={type}
        value={form[k] || ''}
        onChange={(e) => upd(k, e.target.value)}
        placeholder={placeholder}
        className="mt-1"
        data-testid={`payout-${k}`}
      />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="provider-payout-page">
      <Card className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Wallet className="w-5 h-5 text-emerald-700" />
          Datos de cobro
        </h2>
        <p className="text-sm text-slate-700 mt-1">
          Indicá cómo querés recibir tu parte. El CEO usa estos datos para saldarte la deuda
          generada por tus operaciones (50% de ganancia + USDT consumidos).
        </p>
      </Card>

      {/* Método preferido */}
      <Card className="p-5 border-0 shadow-sm">
        <Label className="text-xs uppercase tracking-wide text-slate-500 font-bold">
          Método preferido
        </Label>
        <select
          value={form.preferred_method || ''}
          onChange={(e) => upd('preferred_method', e.target.value)}
          className="mt-1 w-full border rounded-lg h-10 px-3 text-sm"
          data-testid="payout-preferred-method"
        >
          <option value="">Seleccioná un método…</option>
          {METHODS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
      </Card>

      {/* USDT */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-3">
          <Wallet className="w-4 h-4 text-emerald-600" /> Direcciones USDT
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {renderField("usdt_address_trc20", "Dirección TRC20 (Tron)", "T...")}
          {renderField("usdt_address_bep20", "Dirección BEP20 (BSC)", "0x...")}
        </div>
        {renderField("usdt_network_notes", "Notas de red (opcional)", "Ej: usar siempre TRC20, fees bajos")}
      </Card>

      {/* Cuenta UYU */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-3">
          <Banknote className="w-4 h-4 text-blue-600" /> Cuenta UYU (Uruguay)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {renderField("uyu_bank_name", "Banco", "BROU, Itaú, Santander…")}
          {renderField("uyu_account_holder", "Titular", "Nombre completo")}
          {renderField("uyu_account_number", "Nro de cuenta")}
          {renderField("uyu_account_type", "Tipo (CA / CC)", "CA")}
        </div>
      </Card>

      {/* Cuenta USD */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-3">
          <Banknote className="w-4 h-4 text-emerald-600" /> Cuenta USD
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {renderField("usd_bank_name", "Banco")}
          {renderField("usd_account_holder", "Titular")}
          {renderField("usd_account_number", "Nro de cuenta / IBAN")}
        </div>
      </Card>

      {/* Tarjeta cubana */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-3">
          <CreditCard className="w-4 h-4 text-rose-600" /> Tarjeta cubana
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {renderField("cu_card_number", "Nro de tarjeta (16 dígitos)", "9224 ...")}
          {renderField("cu_bank_name", "Banco", "Bandec, BPA…")}
          {renderField("cu_account_holder", "Titular")}
        </div>
      </Card>

      <Card className="p-5 border-0 shadow-sm">
        <Label className="text-xs uppercase tracking-wide text-slate-500 font-bold">Notas para el CEO</Label>
        <textarea
          rows={3}
          value={form.notes || ''}
          onChange={(e) => upd('notes', e.target.value)}
          placeholder="Aclaraciones de método, horarios, montos máximos por canal, etc."
          className="mt-1 w-full border rounded-lg p-3 text-sm"
          data-testid="payout-notes"
        />
      </Card>

      <div className="flex justify-end sticky bottom-3">
        <Button
          onClick={onSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg"
          data-testid="payout-save-btn"
        >
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> : <><Save className="w-4 h-4 mr-2" /> Guardar datos de cobro</>}
        </Button>
      </div>
    </div>
  );
};

export default ProviderPayoutAccountPage;
